import {
  Component,
  OnInit,
  computed,
  inject,
  input,
  signal,
} from '@angular/core';
import { Router, RouterLink, RouterLinkActive } from '@angular/router';
import moment from 'moment';
import { CollapseModule } from 'ngx-bootstrap/collapse';
import { lastValueFrom } from 'rxjs';

import {
  ApiError,
  VehicleSession,
  VehicleSessionService,
} from '../../../../../api';
import { SvgIconComponent } from '../../../../common/components/svg-icon/svg-icon.component';
import { AuthService } from '../../../../states/auth/services/auth.service';
import { FormatConfigurations } from '../../../configurations/FormatConfigurations';
import { CustomVehicleService } from '../../../services/custom-vehicle/custom-vehicle.service';
import { loggedUserHasRight } from '../../../utils/rights.utils';
import { ROUTER_UTILS } from '../../../utils/router.utils';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  imports: [RouterLink, CollapseModule, RouterLinkActive, SvgIconComponent],
})
export class HeaderComponent implements OnInit {
  private router = inject(Router);
  private authService = inject(AuthService);
  private readonly sessionService = inject(VehicleSessionService);
  private readonly customVehicleService = inject(CustomVehicleService);

  readonly showMemu = input(false);

  path = ROUTER_UTILS.config.base;
  reportsPath = ROUTER_UTILS.config.reports;
  public readonly loggedUserHasRight = loggedUserHasRight;

  isCollapsed = true;

  /** The driver's open vehicle session, if any. One read per app load. */
  // Filled in after the session (and the vehicle list) have been awaited, and
  // cleared again after the sign-out request returns.
  public readonly vehicleSession = signal<VehicleSession | null>(null);
  public readonly vehiclePlate = signal('');
  public readonly isVehiclePanelOpen = signal(false);
  public readonly isSigningOutVehicle = signal(false);

  public async ngOnInit(): Promise<void> {
    await this.loadVehicleSession();
  }

  public readonly vehicleSessionStartedAt = computed(() => {
    const session = this.vehicleSession();
    return session
      ? moment(session.started_at).format(FormatConfigurations.TimeFormat)
      : '';
  });

  public onToggleVehiclePanel(): void {
    this.isVehiclePanelOpen.update((open) => !open);
  }

  /**
   * The sign-out path for a driver with no live tag read, which is most of the
   * day. DELETE carries no tag proof, so this always works.
   */
  public async onSignOutVehicle(): Promise<void> {
    if (this.isSigningOutVehicle()) return;

    this.isSigningOutVehicle.set(true);
    try {
      await lastValueFrom(this.sessionService.vehicleSessionClose());
    } catch (e) {
      // 404 means it had already ended — the same outcome the driver wanted.
      if (!(e instanceof ApiError) || e.status !== 404) console.error(e);
    } finally {
      this.isSigningOutVehicle.set(false);
      this.isVehiclePanelOpen.set(false);
      this.vehicleSession.set(null);
    }
  }

  async onClickSignOut(): Promise<void> {
    await this.authService.signOut(undefined, true);
  }

  private async loadVehicleSession(): Promise<void> {
    try {
      // 204 emits undefined despite the generated type saying otherwise.
      this.vehicleSession.set(
        (await lastValueFrom(this.sessionService.vehicleSessionGet())) ?? null,
      );
    } catch (e) {
      console.error(e);
      this.vehicleSession.set(null);
      return;
    }

    const session = this.vehicleSession();
    if (!session) return;

    // No plate, no badge — a UUID means nothing to a driver.
    const vehicles = await this.customVehicleService.vehicleListAllCached();
    this.vehiclePlate.set(
      vehicles.find((v) => v.id === session.vehicle_id)?.vrn ?? '',
    );
  }
}
